import classNames from "classnames/bind";
import * as $ from "jquery";
import * as React from "react";
import { KeyboardEvent } from "react";
import { Keys } from "~/common/enums";
import { isPrintable } from "~/common/keyboardUtils";
import { ViewerTestIdTemplate } from "~/sharedComponents/const";
import { TipIcon } from "~/sharedComponents/tooltip/TipIcon";
import { Marker } from "~/sharedComponents/userflow/utils/marker";
// eslint-disable-next-line @logicsoftware/ep-custom/no-cross-feature-imports
import type { FieldValue } from "~/TimeLogs/weekly/components/Details/DetailsForm";
import { epFormat } from "~/utils/epFormat";
import { memoizeOne } from "../../EasyProjectsCommon/memoizeOne";
import { FieldComponentProps, UpdatableField } from "../../Redux/Form/components/Field";
import { InlinePopupSelectPlainProps } from "../../sharedComponents/select";
import { blockForFullStory, BlockForFullStory } from "../../utils/blockForFullStory";
import { OnKeyAction, WithKeyHandlers } from "../WithKeyHandlers/WithKeyHandlers";
import { ComponentValidationProps } from "../withValidation/withValidation";
import * as styles from "./withEditMode.less";

const classNamesCtx = classNames.bind(styles);

interface WithEditModeState {
    isEditing: boolean;
    isFocused: boolean;
    tempValue: string;
}

export type WithEditModeViewerProps = WithEditModeProps &
    Marker & {
        wrapperClassName: string;
        isFocused: boolean;
        isInvalid?: boolean;
    };

export type WithEditModeEditorProps = WithEditModeProps & {
    /** Closes edit mode and sets focus to viewer */
    onSelect: () => void;
    /** Closes edit mode */
    onBlurAction: () => void;
    /** Closes edit mode and sets focus to viewer */
    onUnmountAction: () => void;
};

type ValueViewerType = React.ComponentType<WithEditModeViewerProps>;

export type WithEditModeProps = ComponentValidationProps &
    UpdatableField &
    BlockForFullStory & {
        /**
         * This function call for returning control to previous state. Should return true if value was reverted
         */
        cancelAction?: () => boolean | undefined | void;
        updateAction?: () => void;
        updateInEditModeAction?: FieldComponentProps["updateInEditModeAction"];
        value: any;
        textValue?: string;
        onBlurAction?: Function;
        onFocusAction?: Function;
        valueFormatter?: F1<FieldValue, string>;
        valueViewer?: ValueViewerType;
        fieldPlaceholder?: string;
        description?: React.ReactNode;
        ignoreDescription?: boolean;
        searchText?: string;
        isFilled?: boolean;
        alwaysEditMode?: boolean;
        isValueChanged?: boolean;
        disabled?: boolean;
        withEditModeRef?: (instance: WithEditModeComponent | null) => void;
        updateOnCtrlEnter?: boolean;
        hideUpdatingIndicator?: boolean;
        isInlineField?: boolean;
        refCallback?: Function;
        ignoreEnter?: boolean;
        /**
         * If true, editor won't close by Esc/Enter.
         * ESC propagation won't be stopped.
         * Usefull for multiselect, because popover subscribes on document's keyboard events.
         */
        ignoreEnterEscInEditMode?: boolean;
        dataTestId?: string;
        editContainerClassName?: string;
    };

export interface WithEditModeComponent {
    closeEditMode: (setFocus?: boolean) => void;
    enterToEditMode: () => void;
    escapeEditMode: () => boolean;
}

const defaultProps: Partial<WithEditModeProps> = {
    isInlineField: true,
};

export const withEditMode = <P extends {}>(UnwrappedComponent: React.ComponentType<P & WithEditModeProps>) =>
    class WithEditMode
        extends React.Component<P & WithEditModeProps, WithEditModeState>
        implements WithEditModeComponent
    {
        static defaultProps: any = defaultProps;

        viewerRef: React.RefObject<HTMLDivElement>;
        editorRef: HTMLElement;
        keyHandledByViewer: boolean;

        constructor(props: P & WithEditModeProps) {
            super(props);

            this.viewerRef = React.createRef();

            this.keyHandledByViewer = false;

            this.state = {
                isEditing: this.props.alwaysEditMode || false,
                isFocused: false,
                tempValue: null,
            };
        }

        onBlur = () => {
            this.setState({ isFocused: false });

            if (this.props.onBlurAction) {
                this.props.onBlurAction();
            }
        };

        onFocus = () => {
            this.setState({ isFocused: true });

            if (this.props.onFocusAction) {
                this.props.onFocusAction();
            }
        };

        keyDownHandler = (e: KeyboardEvent<HTMLDivElement>) => {
            if (e.key === Keys.Enter || isPrintable(e.key)) {
                this.enterEditModeFromView();

                if (e.key === Keys.Enter) {
                    this.keyHandledByViewer = true;
                }
            }
        };

        /**
         * If current mode is 'View', enters into edit mode.
         * Else does nothing.
         */
        enterEditModeFromView = () => {
            if (this.props.disabled) {
                return;
            }

            // check current mode is view
            if (!this.state.isEditing && !this.props.alwaysEditMode) {
                this.setState({
                    isEditing: true,
                });

                if (this.props.updateInEditModeAction) {
                    this.props.updateInEditModeAction(true);
                }
            }
        };

        closeEditMode = (setFocus: boolean = false, setInEditOff: boolean = true) => {
            if (this.props.updateAction) {
                this.props.updateAction();
            }

            // if invalid value is entered left field in edit mode
            if (!!(this.props.value || this.props.textValue) && this.props.invalid) {
                if (setFocus) {
                    this.setEditorFocus();
                }
                return;
            }

            if (!this.props.alwaysEditMode) {
                if (setFocus) {
                    this.setViewerFocus();
                }

                this.setState({
                    isEditing: false,
                });
            }

            if (this.props.updateInEditModeAction && (!this.props.alwaysEditMode || setInEditOff)) {
                this.props.updateInEditModeAction(false);
            }
        };

        enterHandlerForEditMode = (e: KeyboardEvent<HTMLElement>) => {
            if (!this.props.ignoreEnter && !this.props.ignoreEnterEscInEditMode) {
                this.keyHandledByViewer ? (this.keyHandledByViewer = false) : this.closeEditMode(true, false);
            }
        };

        escapeEditMode = () => {
            if (this.props.ignoreEnterEscInEditMode) {
                // see escapeHandlerForEditMode
                // Return false to not prevent esc and close details.
                // The control prevents esc itself for cancel action.
                return false;
            }

            let isCanceled = false;
            if (this.props.cancelAction) {
                if (this.props.cancelAction()) {
                    isCanceled = true;
                }
            }

            if (!this.props.alwaysEditMode) {
                this.setState({
                    isEditing: false,
                });

                this.setViewerFocus();

                if (this.props.updateInEditModeAction) {
                    this.props.updateInEditModeAction(false);
                }
            }

            return isCanceled;
        };

        escapeHandlerForEditMode = (e?: KeyboardEvent<HTMLElement>) => {
            if (this.escapeEditMode()) {
                e.preventDefault(); /* We use this as indicator that we handled event and there is no need to propagate
                 event any further and, for example, close the form on escape. It works to prevent closing details for all entities except for timelogs.*/
            }
        };

        enterToEditMode = () => {
            if (this.editorRef) {
                this.setEditorFocus();
            } else {
                this.setViewerFocus();
            }

            this.enterEditModeFromView();
        };

        setViewerFocus() {
            setTimeout(() => {
                this.viewerRef.current?.focus();
            }, 0);
        }

        setEditorFocus() {
            if (this.editorRef) {
                $(this.editorRef).focus();
            }
        }

        onClick = () => {
            this.enterEditModeFromView();
        };

        handleEditorEnterKeyDown = (e: React.KeyboardEvent) => {
            if (this.props.updateOnCtrlEnter && e.ctrlKey) {
                e.preventDefault();
            }
        };

        _formEditorKeyDownHandlers = (): OnKeyAction[] => {
            const action: OnKeyAction = {
                key: Keys.Enter,
                action: this.handleEditorEnterKeyDown,
                stopPropagation: true,
            };

            return [action];
        };

        _formEditorKeyUpHandlers = () => {
            const { isEditing } = this.state;
            const { alwaysEditMode, updateOnCtrlEnter } = this.props;

            const escapeEventHandler: OnKeyAction = {
                key: Keys.Escape,
                action: (e: KeyboardEvent<HTMLElement>) => this.escapeHandlerForEditMode(e),
                stopPropagation: false,
            };

            const enterEventHandler: OnKeyAction = {
                key: Keys.Enter,
                withCtrlKey: updateOnCtrlEnter,
                action: (e: KeyboardEvent<HTMLElement>) => this.enterHandlerForEditMode(e),
                stopPropagation: true,
            };

            // stopPropagation here is to prevent timelog details to close on escape when there is unsaved changes in field
            // for the details of other entities this functionality is implemented through "preventDefault"
            // and can be removed if timelogs would be rewritten
            if ((!alwaysEditMode && isEditing) || (this.props.isValueChanged && alwaysEditMode)) {
                if (!this.props.ignoreEnterEscInEditMode /* popover should handle escape key on its own */) {
                    escapeEventHandler.stopPropagation = true;
                }
            }

            return [escapeEventHandler, enterEventHandler];
        };

        _formPlaceholderClasses = () => {
            const { isFocused, isEditing } = this.state;
            const { isFilled, alwaysEditMode, disabled } = this.props;

            const placeholderClasses = [];

            if (isFilled) {
                placeholderClasses.push(styles.filled);
                placeholderClasses.push(this.props.editContainerClassName);
            }
            if (isFocused) {
                placeholderClasses.push(styles.focus);

                if (isEditing) {
                    placeholderClasses.push(styles.editing);
                    placeholderClasses.push(this.props.editContainerClassName);
                }
            } else if (!alwaysEditMode && isEditing) {
                placeholderClasses.push(styles.editing);
            }

            if (disabled) {
                placeholderClasses.push(styles.disabled);
            }

            return placeholderClasses;
        };

        renderViewer() {
            const { isFocused } = this.state;
            let { value, showValidation, valueFormatter, valueViewer } = this.props;
            if (this.state.tempValue) {
                value = this.state.tempValue;
            }

            const ValueViewer = valueViewer as ValueViewerType;

            const inputClasses = [styles.editvalue];

            if (showValidation) {
                inputClasses.push(styles.error);
            }

            const wrapperClass = classNamesCtx(...inputClasses);
            const testId = epFormat(ViewerTestIdTemplate, this.props.fieldPlaceholder);

            if (!ValueViewer) {
                return (
                    <div data-testid={testId} className={wrapperClass}>
                        {valueFormatter ? valueFormatter(value) : value}
                    </div>
                );
            }

            return (
                <ValueViewer
                    {...this.props}
                    isFocused={isFocused}
                    isInvalid={showValidation}
                    value={value}
                    wrapperClassName={wrapperClass}
                    data-testid={testId}
                />
            );
        }

        wrapSelectCreateAction = memoizeOne((originalFunc: any) => {
            if (originalFunc) {
                return (text: string, callback: F1<number, void>) => {
                    this.setState({
                        tempValue: text,
                    });

                    originalFunc(text, (newItem: number) => {
                        callback(newItem);
                        this.setState({
                            tempValue: null,
                        });
                    });
                };
            }

            return originalFunc;
        });

        renderEditor() {
            let value = this.props.value;

            const props = Object.assign(
                {
                    ["aria-label"]: this.props.fieldPlaceholder,
                },
                this.props,
                {
                    value,
                    onSelect: () => this.closeEditMode(true),
                    onBlurAction: () => this.closeEditMode(),
                    onFocusAction: () => {
                        if (this.props.updateInEditModeAction) {
                            this.props.updateInEditModeAction(true);
                        }
                    },
                    onUnmountAction: () => this.closeEditMode(),
                    refCallback: (ref: HTMLElement) => {
                        this.editorRef = ref;
                        if (this.props.refCallback) {
                            this.props.refCallback(ref);
                        }
                    },
                }
            );

            type SelectProps = InlinePopupSelectPlainProps<any, any>;
            const createAction = (props as any as SelectProps).createAction;
            if (createAction) {
                (props as any as SelectProps).createAction = this.wrapSelectCreateAction(createAction);
            }

            return (
                <WithKeyHandlers
                    actionsOnKeyDown={this._formEditorKeyDownHandlers()}
                    actionsOnKeyUp={this._formEditorKeyUpHandlers()}
                >
                    <UnwrappedComponent {...props} />
                </WithKeyHandlers>
            );
        }

        render() {
            const { isEditing } = this.state;
            const { disabled } = this.props;

            const wrapperClass = this.props.isInlineField ? styles.wrapper : undefined;

            return (
                <div className={wrapperClass}>
                    <div
                        className={classNamesCtx(...this._formPlaceholderClasses())}
                        ref={this.viewerRef}
                        tabIndex={isEditing === false && !disabled ? 0 : undefined}
                        onBlur={this.onBlur}
                        onFocus={this.onFocus}
                        onClick={this.onClick}
                        onKeyDown={this.keyDownHandler}
                        data-testid={this.props.dataTestId}
                    >
                        <div className={blockForFullStory(classNamesCtx(styles.editmodeWrapper), this.props)}>
                            {isEditing ? this.renderEditor() : this.renderViewer()}
                        </div>
                        {/*
                            placeholder should be rendered after control component because in case
                            if editor control has relative position then placeholder will be shown under
                            editor and won't be visible (this is case at least for InlinePopupSelect)
                        */}
                        <span className={styles.placeholder}>
                            <span className={styles.placeholder_text}>{this.props.fieldPlaceholder}</span>
                            {this.props.description && !this.props.ignoreDescription && (
                                <TipIcon className={styles.placeholder_info}>{this.props.description}</TipIcon>
                            )}
                        </span>
                        {this.renderSavingIndicator()}
                    </div>
                </div>
            );
        }

        renderSavingIndicator() {
            const { isUpdating, hideUpdatingIndicator } = this.props;
            return isUpdating && !hideUpdatingIndicator && <div role="progressbar" className={styles.saving} />;
        }

        componentDidMount() {
            if (this.props.withEditModeRef) {
                this.props.withEditModeRef(this);
            }
        }

        componentWillUnmount() {
            if (this.props.withEditModeRef) {
                this.props.withEditModeRef(null);
            }
        }
    };
